import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { fleetService } from '../../services/fleetService';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  SelectChangeEvent,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Vehicle,
  FuelType,
  TransmissionType,
  VehicleStatus,
  FinanceType,
  VehicleType,
  VehicleUsageType,
  TechnicalDetails
} from '../../types/vehicle';
import { Fleet } from '../../types';
import { Timestamp } from 'firebase/firestore';
import vehicleTechnicalService, { VehicleTechnicalService } from '../../services/vehicleTechnicalService';

interface VehicleFormStepperProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (vehicleData: Partial<Vehicle>) => Promise<void>;
  vehicle?: Vehicle;
  title: string;
}

interface RenderProps {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: CarBrandOption | string;
}

interface AutocompleteRenderProps {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: CarModelOption | string;
}

const FUEL_TYPES: FuelType[] = ['gasoline', 'diesel', 'hybrid', 'electric'];
const TRANSMISSION_TYPES: TransmissionType[] = ['manual', 'automatic'];
const VEHICLE_STATUSES: VehicleStatus[] = ['active', 'inactive', 'sold'];
const FINANCE_TYPES: FinanceType[] = ['purchase', 'lease'];
const VEHICLE_USAGE_TYPES: VehicleUsageType[] = ['SERVICE', 'COMPANY'];
const CAR_BRANDS = {
  EUROPEAN: [
    'Alfa Romeo', 'Aston Martin', 'Audi', 'Bentley', 'BMW', 'Bugatti',
    'Citroën', 'Cupra', 'Dacia', 'DS', 'Ferrari', 'Fiat', 'Jaguar', 'Lamborghini',
    'Lancia', 'Land Rover', 'Lotus', 'Maserati', 'McLaren', 'Mercedes-Benz',
    'Mini', 'Opel', 'Peugeot', 'Porsche', 'Renault', 'Rolls-Royce', 'Saab',
    'SEAT', 'Škoda', 'Smart', 'Volkswagen', 'Volvo'
  ],
  JAPANESE: [
    'Acura', 'Daihatsu', 'Honda', 'Infiniti', 'Isuzu', 'Lexus', 'Mazda',
    'Mitsubishi', 'Nissan', 'Subaru', 'Suzuki', 'Toyota'
  ],
  KOREAN: [
    'Genesis', 'Hyundai', 'Kia', 'SsangYong'
  ],
  AMERICAN: [
    'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge', 'Ford',
    'GMC', 'Hummer', 'Jeep', 'Lincoln', 'Ram', 'Rivian', 'Tesla'
  ],
  CHINESE: [
    'Aiways', 'BYD', 'Changan', 'Dongfeng', 'FAW', 'Geely', 'Great Wall',
    'Haval', 'Hongqi', 'Lynk & Co', 'MG', 'NIO', 'Polestar', 'SAIC', 'Wuling', 'Xpeng'
  ],
  INDIAN: [
    'Mahindra', 'Tata'
  ]
};

interface CarBrandOption {
  brand: string;
  region: string;
}

interface CarModelsByCategory {
  [category: string]: string[];
}

interface CarModelsDatabase {
  [brand: string]: CarModelsByCategory;
}

const CAR_MODELS: CarModelsDatabase = {
  // Marques françaises
  'Peugeot': {
    'SUV & Crossover': ['2008', '3008', '5008'],
    'Berlines': ['208', '308', '408', '508'],
    'Utilitaires': ['Partner', 'Expert', 'Boxer', 'Rifter', 'Traveller'],
    'Anciens Modèles': ['207', '307', '407', '4007', '4008', 'RCZ', 'Ion']
  },
  'Renault': {
    'SUV & Crossover': ['Captur', 'Arkana', 'Austral', 'Espace', 'Koleos'],
    'Berlines': ['Clio', 'Megane', 'Scenic', 'Talisman'],
    'Utilitaires': ['Kangoo', 'Trafic', 'Master'],
    'Électriques': ['Zoe', 'Megane E-Tech', 'Twingo Electric', '5 E-Tech'],
    'Anciens Modèles': ['Kadjar', 'Laguna', 'Twingo', 'Modus', 'Fluence']
  },
  'Citroën': {
    'SUV & Crossover': ['C3 Aircross', 'C4', 'C5 Aircross', 'C5 X'],
    'Berlines': ['C3', 'C-Elysée'],
    'Utilitaires': ['Berlingo', 'Jumpy', 'Jumper', 'SpaceTourer'],
    'Électriques': ['ë-C4', 'ë-Berlingo', 'ë-SpaceTourer'],
    'Anciens Modèles': ['C1', 'C4 Cactus', 'C5', 'C6', 'Xsara Picasso']
  },
  'Dacia': {
    'SUV & Crossover': ['Duster', 'Jogger', 'Spring'],
    'Berlines': ['Sandero', 'Logan'],
    'Utilitaires': ['Dokker']
  },
  'DS': {
    'SUV & Crossover': ['DS 3 Crossback', 'DS 7 Crossback'],
    'Berlines': ['DS 4', 'DS 9'],
    'Électriques': ['DS 3 E-Tense', 'DS 7 E-Tense', 'DS 4 E-Tense']
  },
  'Alpine': {
    'Sportives': ['A110', 'A110 S', 'A110 GT']
  },

  // Marques allemandes
  'Volkswagen': {
    'SUV & Crossover': ['T-Cross', 'T-Roc', 'Taigo', 'Tiguan', 'Touareg', 'ID.4', 'ID.5'],
    'Berlines': ['Polo', 'Golf', 'Arteon', 'Passat', 'ID.3', 'ID.7'],
    'Utilitaires': ['Caddy', 'Transporter', 'Crafter', 'ID.Buzz'],
    'Sportives': ['Golf GTI', 'Golf R']
  },
  'Audi': {
    'SUV & Crossover': ['Q2', 'Q3', 'Q4 e-tron', 'Q5', 'Q7', 'Q8', 'e-tron'],
    'Berlines': ['A1', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8'],
    'Sportives': ['TT', 'R8', 'RS3', 'RS4', 'RS5', 'RS6', 'RS7']
  },
  'BMW': {
    'SUV & Crossover': ['X1', 'X2', 'X3', 'X4', 'X5', 'X6', 'X7', 'iX', 'iX1', 'iX3'],
    'Berlines': ['Série 1', 'Série 2', 'Série 3', 'Série 4', 'Série 5', 'Série 6', 'Série 7', 'Série 8', 'i4', 'i5', 'i7'],
    'Sportives': ['M2', 'M3', 'M4', 'M5', 'M8', 'Z4']
  },
  'Mercedes-Benz': {
    'SUV & Crossover': ['GLA', 'GLB', 'GLC', 'GLE', 'GLS', 'EQA', 'EQB', 'EQC', 'EQE SUV', 'EQS SUV'],
    'Berlines': ['Classe A', 'Classe C', 'Classe E', 'Classe S', 'CLA', 'CLS', 'EQE', 'EQS'],
    'Sportives': ['AMG GT', 'AMG A 45', 'AMG C 63', 'AMG E 63']
  },
  'Opel': {
    'SUV & Crossover': ['Crossland', 'Grandland', 'Mokka'],
    'Berlines': ['Corsa', 'Astra', 'Insignia'],
    'Utilitaires': ['Combo', 'Vivaro', 'Movano'],
    'Électriques': ['Corsa-e', 'Mokka-e', 'Combo-e']
  },
  'Porsche': {
    'SUV & Crossover': ['Macan', 'Cayenne', 'Taycan Cross Turismo'],
    'Sportives': ['718 Cayman', '718 Boxster', '911', 'Taycan', 'Panamera']
  },
  'Mini': {
    'Berlines': ['Mini 3 portes', 'Mini 5 portes'],
    'SUV & Crossover': ['Countryman', 'Clubman'],
    'Électriques': ['Cooper SE']
  },
  'Škoda': {
    'SUV & Crossover': ['Kamiq', 'Karoq', 'Kodiaq', 'Enyaq'],
    'Berlines': ['Fabia', 'Scala', 'Octavia', 'Superb']
  },

  // Marques japonaises
  'Toyota': {
    'SUV & Crossover': ['Yaris Cross', 'C-HR', 'RAV4', 'Highlander', 'Land Cruiser', 'bZ4X'],
    'Berlines': ['Yaris', 'Corolla', 'Camry', 'Mirai'],
    'Utilitaires': ['Proace', 'Proace City', 'Hilux'],
    'Hybrides': ['Prius', 'RAV4 Hybrid', 'Corolla Hybrid', 'Yaris Hybrid']
  },
  'Honda': {
    'SUV & Crossover': ['HR-V', 'CR-V', 'ZR-V', 'e:Ny1'],
    'Berlines': ['Jazz', 'Civic', 'Accord'],
    'Électriques': ['e', 'e:Ny1']
  },
  'Nissan': {
    'SUV & Crossover': ['Juke', 'Qashqai', 'X-Trail', 'Ariya'],
    'Berlines': ['Micra', 'Leaf'],
    'Utilitaires': ['NV200', 'NV300', 'NV400', 'Townstar']
  },
  'Mazda': {
    'SUV & Crossover': ['CX-3', 'CX-30', 'CX-5', 'CX-60', 'MX-30'],
    'Berlines': ['Mazda2', 'Mazda3', 'Mazda6'],
    'Sportives': ['MX-5']
  },
  'Suzuki': {
    'SUV & Crossover': ['Ignis', 'Vitara', 'S-Cross'],
    'Berlines': ['Swift', 'Baleno'],
    'Utilitaires': ['Jimny']
  },
  'Subaru': {
    'SUV & Crossover': ['XV', 'Forester', 'Outback', 'Solterra'],
    'Sportives': ['BRZ', 'WRX']
  },
  'Mitsubishi': {
    'SUV & Crossover': ['ASX', 'Eclipse Cross', 'Outlander'],
    'Utilitaires': ['L200']
  },
  'Lexus': {
    'SUV & Crossover': ['UX', 'NX', 'RX', 'RZ'],
    'Berlines': ['IS', 'ES', 'LS'],
    'Sportives': ['LC']
  },

  // Marques coréennes
  'Hyundai': {
    'SUV & Crossover': ['Bayon', 'Kona', 'Tucson', 'Santa Fe', 'Ioniq 5', 'Ioniq 6'],
    'Berlines': ['i10', 'i20', 'i30', 'Ioniq'],
    'Électriques': ['Ioniq 5', 'Ioniq 6', 'Kona Electric']
  },
  'Kia': {
    'SUV & Crossover': ['Stonic', 'Niro', 'Sportage', 'Sorento', 'EV6', 'EV9'],
    'Berlines': ['Picanto', 'Rio', 'Ceed', 'Stinger', 'EV6'],
    'Électriques': ['e-Niro', 'e-Soul', 'EV6', 'EV9']
  },

  // Marques américaines
  'Ford': {
    'SUV & Crossover': ['Puma', 'Kuga', 'Explorer', 'Mustang Mach-E'],
    'Berlines': ['Fiesta', 'Focus', 'Mondeo'],
    'Utilitaires': ['Transit', 'Transit Custom', 'Transit Connect', 'Ranger'],
    'Sportives': ['Mustang']
  },
  'Tesla': {
    'Électriques': ['Model 3', 'Model S', 'Model X', 'Model Y']
  },
  'Jeep': {
    'SUV & Crossover': ['Avenger', 'Renegade', 'Compass', 'Cherokee', 'Grand Cherokee', 'Wrangler']
  },

  // Marques italiennes
  'Fiat': {
    'Berlines': ['500', 'Panda', 'Tipo'],
    'Électriques': ['500e'],
    'Utilitaires': ['Doblo', 'Ducato', 'Fiorino']
  },
  'Alfa Romeo': {
    'SUV & Crossover': ['Tonale', 'Stelvio'],
    'Berlines': ['Giulia', 'Giulietta']
  },

  // Marques britanniques
  'Land Rover': {
    'SUV & Crossover': ['Defender', 'Discovery', 'Discovery Sport', 'Range Rover', 'Range Rover Sport', 'Range Rover Evoque', 'Range Rover Velar']
  },
  'Jaguar': {
    'SUV & Crossover': ['E-Pace', 'F-Pace', 'I-Pace'],
    'Berlines': ['XE', 'XF'],
    'Sportives': ['F-Type']
  },

  // Marques suédoises
  'Volvo': {
    'SUV & Crossover': ['XC40', 'XC60', 'XC90', 'C40 Recharge'],
    'Berlines': ['S60', 'S90', 'V60', 'V90'],
    'Électriques': ['XC40 Recharge', 'C40 Recharge']
  },
  'Polestar': {
    'Électriques': ['Polestar 2', 'Polestar 3', 'Polestar 4']
  },

  // Marques chinoises
  'BYD': {
    'SUV & Crossover': ['Atto 3', 'Tang', 'Seal U'],
    'Berlines': ['Seal', 'Dolphin', 'Han']
  },
  'MG': {
    'SUV & Crossover': ['MG ZS', 'MG HS', 'MG EHS', 'MG Marvel R'],
    'Berlines': ['MG4', 'MG5'],
    'Électriques': ['MG4 Electric', 'MG5 Electric', 'MG ZS EV']
  }
};

interface CarModelOption {
  model: string;
  category: string;
}

// Prepare brands for Autocomplete options
const ALL_CAR_BRANDS: CarBrandOption[] = Object.entries(CAR_BRANDS).flatMap(([region, brands]) => 
  brands.map(brand => ({
    brand,
    region
  }))
);

// Helper function to get models for a brand
const getModelsForBrand = (brand: string): CarModelOption[] => {
  if (!brand) return [];
  const brandModels = CAR_MODELS[brand];
  if (!brandModels) return [];

  return Object.entries(brandModels).flatMap(([category, models]) =>
    models.map(model => ({
      model,
      category
    }))
  );
};

// Type guard pour vérifier si une valeur est une CarBrandOption
const isCarBrandOption = (value: CarBrandOption | string | null): value is CarBrandOption => {
  return value !== null && typeof value === 'object' && 'brand' in value;
};

// Type guard pour vérifier si une valeur est une CarModelOption
const isCarModelOption = (value: CarModelOption | string | null): value is CarModelOption => {
  return value !== null && typeof value === 'object' && 'model' in value;
};

const VEHICLE_TYPES: VehicleType[] = [
  'PERSONAL_VEHICLE',
  'SUV_4X4',
  'COLLECTION',
  'GPL_VEHICLE',
  'ELECTRIC_HYBRID',
  'LIGHT_COMMERCIAL',
  'CAMPING_CAR',
  'MOTORCYCLE',
  'TAXI_VTC',
  'MEDICAL_TRANSPORT',
  'BREAKDOWN_VEHICLE'
];

export const VehicleFormStepper = ({
  open,
  onClose,
  onSubmit,
  vehicle,
  title,
}: VehicleFormStepperProps): JSX.Element => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const technicalService = new VehicleTechnicalService();
  const [fleets, setFleets] = useState<Fleet[]>([]);

  // Charger les flottes
  useEffect(() => {
    const loadFleets = async () => {
      if (!user?.organizationId) return;
      try {
        const fleetsList = await fleetService.getFleets(user.organizationId);
        setFleets(fleetsList);
      } catch (err) {
        console.error('Error loading fleets:', err);
      }
    };
    
    loadFleets();
  }, [user?.organizationId]);

  const [formData, setFormData] = useState<Partial<Vehicle>>(() => {
    if (vehicle) return vehicle;

    return {
      status: 'active',
      currentMileage: 0,
      totalRepairCosts: 0,
      totalFuelCosts: 0,
      annualMaintenanceCost: 0,
      annualDepreciation: 0,
      enginePower: 0,
      doors: 0,
      seats: 0,
      transmission: 'manual' as TransmissionType,
      vehicleType: 'PERSONAL_VEHICLE' as VehicleType,
      documents: [],
      firstRegistrationDate: Timestamp.now(),
      insurance: {
        provider: '',
        policyNumber: '',
        expirationDate: Timestamp.now()
      }
    };
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchingTechnicalDetails, setFetchingTechnicalDetails] = useState(false);
  const [technicalDetailsError, setTechnicalDetailsError] = useState<string | null>(null);
  const [lastSearchedPlate, setLastSearchedPlate] = useState<string>('');

  const steps = [
    {
      label: t('vehicle.form.steps.basicInfo'),
      description: t('vehicle.form.steps.basicInfoDesc'),
    },
    {
      label: t('vehicle.form.steps.additionalInfo'),
      description: t('vehicle.form.steps.additionalInfoDesc'),
    },
  ];

  const handleNext = () => {
    if (validateBasicInfo()) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateBasicInfo = (): boolean => {
    const requiredFields = [
      { field: 'licensePlate', label: 'vehicle.licensePlate' },
      { field: 'make', label: 'vehicle.make' },
      { field: 'model', label: 'vehicle.model' },
      { field: 'year', label: 'vehicle.year' },
      { field: 'firstRegistrationDate', label: 'vehicle.firstRegistrationDate' },
      { field: 'fuelType', label: 'vehicle.technical.fuel' },
      { field: 'vehicleType', label: 'vehicle.type' }
    ];

    for (const { field, label } of requiredFields) {
      const value = formData[field as keyof typeof formData];
      // Vérifier si la valeur existe et n'est pas une chaîne vide après trim
      if (!value || (typeof value === 'string' && !value.trim())) {
        setError(t('vehicle.form.errors.fieldRequired', { field: t(label) }));
        return false;
      }
    }

    setError(null);
    return true;
  };

  const handleChange = (field: keyof Vehicle) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData((prev: Partial<Vehicle>) => ({
      ...prev,
      [field]: event.target.value
    }));

    if (field === 'licensePlate' && typeof event.target.value === 'string' && 
        event.target.value.length >= 7 && event.target.value !== lastSearchedPlate) {
      fetchTechnicalDetails(event.target.value);
    }
  };

  const handleSelectChange = (field: keyof Vehicle) => (
    event: SelectChangeEvent
  ) => {
    setFormData((prev: Partial<Vehicle>) => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleNumberChange = (field: keyof Vehicle) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseFloat(event.target.value);
    if (!isNaN(value)) {
      setFormData((prev: Partial<Vehicle>) => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDateChange = (field: keyof Vehicle) => (date: Date | null) => {
    if (date) {
      setFormData((prev: Partial<Vehicle>) => ({
        ...prev,
        [field]: Timestamp.fromDate(date)
      }));
    }
  };

  const fetchTechnicalDetails = async (licensePlate: string): Promise<void> => {
    setFetchingTechnicalDetails(true);
    setTechnicalDetailsError(null);
    setLastSearchedPlate(licensePlate);

    try {
      const details = await VehicleTechnicalService.getVehicleDetailsByLicensePlate(licensePlate);
      if (details?.manufacturerDetails?.make &&
          details.manufacturerDetails.model &&
          details.manufacturerDetails.year) {
            
        // Créer un objet manufacturerDetails garanti non-null
        const manufacturerDetails = {
          make: details.manufacturerDetails.make,
          model: details.manufacturerDetails.model,
          year: details.manufacturerDetails.year
        } as const;

        // Créer l'objet technicalDetails avec manufacturerDetails garanti
        const technicalDetails: TechnicalDetails = {
          manufacturerDetails,
          enginePowerPS: details.enginePowerPS,
          ptac: details.ptac
        };

        // Mettre à jour le state avec les données garanties
        setFormData((prev: Partial<Vehicle>) => ({
          ...prev,
          make: manufacturerDetails.make,
          model: manufacturerDetails.model,
          year: manufacturerDetails.year,
          enginePower: details.enginePowerPS || 0,
          technicalDetails
        }));
      }
    } catch (err) {
      setTechnicalDetailsError(t('vehicle.form.errors.fetchDetailsFailed'));
      console.error('Error fetching technical details:', err);
    } finally {
      setFetchingTechnicalDetails(false);
    }
  };

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (activeStep === 0 && !validateBasicInfo()) {
      return;
    }

    // Validate PTAC for light commercial vehicles at final submission
    if (activeStep === 1 && formData.vehicleType === 'LIGHT_COMMERCIAL' && !formData.technicalDetails?.ptac) {
      setError(t('vehicle.form.errors.ptacRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const finalFormData = {
        ...formData,
        status: formData.status || 'active',
        currentMileage: formData.currentMileage || 0,
        totalRepairCosts: formData.totalRepairCosts || 0,
        totalFuelCosts: formData.totalFuelCosts || 0,
        annualMaintenanceCost: formData.annualMaintenanceCost || 0,
        annualDepreciation: formData.annualDepreciation || 0,
        enginePower: formData.enginePower || 0,
        doors: formData.doors || 0,
        seats: formData.seats || 0,
        transmission: formData.transmission || 'manual',
        documents: formData.documents || [],
        insurance: {
          provider: formData.insurance?.provider || '',
          policyNumber: formData.insurance?.policyNumber || '',
          expirationDate: formData.insurance?.expirationDate || Timestamp.now()
        }
      } as Partial<Vehicle>;

      // Initialize technical inspection with actual vehicle data
      finalFormData.technicalInspection = technicalService.initializeTechnicalInspection(
        finalFormData.firstRegistrationDate || Timestamp.now(),
        finalFormData.vehicleType || 'PERSONAL_VEHICLE',
        finalFormData.fuelType as FuelType,
        finalFormData.year as number,
        finalFormData.technicalDetails
      );
      
      await onSubmit(finalFormData);
      onClose();
    } catch (err) {
      console.error('Error submitting vehicle form:', err);
      setError(err instanceof Error ? err.message : t('vehicle.form.errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  }, [activeStep, formData, onClose, onSubmit, t, technicalService, validateBasicInfo]);

  const renderBasicInfo = (): JSX.Element => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          {steps[0].description}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.licensePlate')}
          value={formData.licensePlate || ''}
          onChange={handleChange('licensePlate')}
          required
          error={!!error && error.includes('immatriculation')}
          helperText={
            error?.includes('immatriculation')
              ? error
              : (fetchingTechnicalDetails ? t('vehicle.form.fetchingDetails') : '')
          }
          InputProps={{
            endAdornment: fetchingTechnicalDetails && <CircularProgress size={20} />
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.vin')}
          value={formData.vin || ''}
          onChange={handleChange('vin')}
          error={formData.vin !== undefined && formData.vin.length > 0 && formData.vin.length !== 17}
          helperText={formData.vin !== undefined && formData.vin.length > 0 && formData.vin.length !== 17 ? t('vehicle.form.errors.invalidVin') : ''}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          freeSolo
          options={ALL_CAR_BRANDS}
          getOptionLabel={(option: CarBrandOption | string): string =>
            typeof option === 'string' ? option : option.brand
          }
          groupBy={(option: CarBrandOption | string): string =>
            typeof option === 'string' ? '' : t(`vehicle.brands.${option.region.toLowerCase()}`)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('vehicle.make')}
              required
              error={!!error && error.includes(t('vehicle.make'))}
            />
          )}
          value={formData.make ? { brand: formData.make, region: '' } : null}
          onInputChange={(_, newInputValue: string) => {
            // Capture la valeur saisie même si elle ne correspond pas aux options
            setFormData((prev: Partial<Vehicle>) => ({
              ...prev,
              make: newInputValue
            }));
          }}
          onChange={(_, newValue: CarBrandOption | string | null) => {
            const brand = typeof newValue === 'string' ? newValue : newValue?.brand || '';
            setFormData((prev: Partial<Vehicle>) => ({
              ...prev,
              make: brand
            }));
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: CarBrandOption | string) => (
            <li {...props}>
              {typeof option === 'string' ? option : option.brand}
            </li>
          )}
          isOptionEqualToValue={(option: CarBrandOption | string, value: CarBrandOption | string | null): boolean => {
            if (value === null) return false;
            if (typeof option === 'string' && typeof value === 'string') return option === value;
            if (typeof option === 'string') return false;
            if (typeof value === 'string') return option.brand === value;
            return option.brand === value.brand;
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Autocomplete
          freeSolo
          options={formData.make ? getModelsForBrand(formData.make) : []}
          getOptionLabel={(option: CarModelOption | string): string =>
            typeof option === 'string' ? option : option.model
          }
          groupBy={(option: CarModelOption | string): string =>
            typeof option === 'string' ? '' : option.category
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('vehicle.model')}
              required
              error={!!error && error.includes(t('vehicle.model'))}
            />
          )}
          value={formData.model || null}
          onInputChange={(_, newInputValue: string) => {
            // Capture la valeur saisie même si elle ne correspond pas aux options
            setFormData((prev: Partial<Vehicle>) => ({
              ...prev,
              model: newInputValue
            }));
          }}
          onChange={(_, newValue: CarModelOption | string | null) => {
            const model = typeof newValue === 'string' ? newValue : newValue?.model || '';
            setFormData((prev: Partial<Vehicle>) => ({
              ...prev,
              model
            }));
          }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: CarModelOption | string) => (
            <li {...props}>
              {typeof option === 'string' ? option : option.model}
            </li>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.year')}
          type="number"
          value={formData.year || ''}
          onChange={handleNumberChange('year')}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={t('vehicle.firstRegistrationDate')}
          value={formData.firstRegistrationDate?.toDate() || null}
          onChange={handleDateChange('firstRegistrationDate')}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>{t('vehicle.technical.fuel')}</InputLabel>
          <Select
            value={formData.fuelType || ''}
            onChange={handleSelectChange('fuelType')}
            label={t('vehicle.technical.fuel')}
          >
            {FUEL_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {t(`vehicle.technical.fuelType.${type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>{t('vehicle.type')}</InputLabel>
          <Select
            value={formData.vehicleType || ''}
            onChange={handleSelectChange('vehicleType')}
            label={t('vehicle.type')}
          >
            {VEHICLE_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {t(`vehicle.type.${type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderAdditionalInfo = (): JSX.Element => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          {steps[1].description}
        </Typography>
      </Grid>
      
      {/* Technical Details */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          {t('vehicle.technical.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.technical.power')}
          type="number"
          value={formData.enginePower || ''}
          onChange={handleNumberChange('enginePower')}
        />
      </Grid>
      {formData.vehicleType === 'LIGHT_COMMERCIAL' && (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('vehicle.technical.ptac')}
            type="number"
            value={formData.technicalDetails?.ptac || ''}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              setFormData((prev: Partial<Vehicle>) => ({
                ...prev,
                technicalDetails: {
                  ...prev.technicalDetails!,
                  ptac: value
                }
              }));
            }}
            required
            error={!formData.technicalDetails?.ptac}
            helperText={!formData.technicalDetails?.ptac ? t('vehicle.form.errors.ptacRequired') : ''}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>{t('vehicle.technical.transmission')}</InputLabel>
          <Select
            value={formData.transmission || ''}
            onChange={handleSelectChange('transmission')}
            label={t('vehicle.technical.transmission')}
          >
            {TRANSMISSION_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {t(`vehicle.technical.transmissionType.${type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.technical.doors')}
          type="number"
          value={formData.doors || ''}
          onChange={handleNumberChange('doors')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.technical.seats')}
          type="number"
          value={formData.seats || ''}
          onChange={handleNumberChange('seats')}
        />
      </Grid>

      {/* Status and Finance */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          {t('vehicle.status.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>{t('vehicle.status.title')}</InputLabel>
          <Select
            value={formData.status || ''}
            onChange={handleSelectChange('status')}
            label={t('vehicle.status.title')}
          >
            {VEHICLE_STATUSES.map(status => (
              <MenuItem key={status} value={status}>
                {t(`vehicle.status.${status}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>{t('vehicle.financing.title')}</InputLabel>
          <Select
            value={formData.financeType || ''}
            onChange={handleSelectChange('financeType')}
            label={t('vehicle.financing.title')}
          >
            {FINANCE_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {t(`vehicle.financing.${type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Current Mileage */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          {t('vehicle.mileage.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.mileage.current')}
          type="number"
          value={formData.currentMileage || 0}
          onChange={handleNumberChange('currentMileage')}
          helperText={t('vehicle.mileage.optional')}
        />
      </Grid>

      {/* Fleet */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          {t('fleet.management')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>{t('vehicle.usage.title')}</InputLabel>
          <Select
            value={formData.usageType || ''}
            onChange={handleSelectChange('usageType')}
            label={t('vehicle.usage.title')}
          >
            <MenuItem value="">
              <em>{t('common.noOptions')}</em>
            </MenuItem>
            {VEHICLE_USAGE_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {t(`vehicle.usage.${type}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>{t('vehicle.fleet')}</InputLabel>
          <Select
            value={formData.fleetId || ''}
            onChange={handleSelectChange('fleetId')}
            label={t('vehicle.fleet')}
          >
            <MenuItem value="">{t('vehicle.noFleet')}</MenuItem>
            {fleets.map((fleet: Fleet) => (
              <MenuItem key={fleet.id} value={fleet.id}>
                {fleet.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Insurance Information */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          {t('vehicle.insurance.title')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.insurance.company')}
          value={formData.insurance?.provider || ''}
          onChange={(e) => setFormData((prev: Partial<Vehicle>) => ({
            ...prev,
            insurance: {
              ...prev.insurance!,
              provider: e.target.value
            }
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label={t('vehicle.insurance.policyNumber')}
          value={formData.insurance?.policyNumber || ''}
          onChange={(e) => setFormData((prev: Partial<Vehicle>) => ({
            ...prev,
            insurance: {
              ...prev.insurance!,
              policyNumber: e.target.value
            }
          }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label={t('vehicle.insurance.expiryDate')}
          value={formData.insurance?.expirationDate?.toDate() || null}
          onChange={(date: Date | null) => setFormData((prev: Partial<Vehicle>) => ({
            ...prev,
            insurance: {
              ...prev.insurance!,
              expirationDate: date ? Timestamp.fromDate(date) : Timestamp.now()
            }
          }))}
          slotProps={{
            textField: { fullWidth: true }
          }}
        />
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {title}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {technicalDetailsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {technicalDetailsError}
            </Alert>
          )}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 ? renderBasicInfo() : renderAdditionalInfo()}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {activeStep === 0 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
            >
              {t('vehicle.form.steps.next')}
            </Button>
          ) : (
            <React.Fragment>
              <Button onClick={handleBack}>
                {t('vehicle.form.steps.back')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
              >
                {loading ? t('common.saving') : t('common.save')}
              </Button>
            </React.Fragment>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
